import { ChevronRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * A fact row you can walk into — TROOP-120.
 *
 * SPEC-GAP, on the same terms as {@link DocketRow}: §6 lists no navigation row, so per §10
 * ("prefer the nearest specified pattern") this is `FactRow`'s layout with a right chevron and a
 * button wrapper, and nothing else — the same 100px label column, the same `--text-label` muted
 * label, the same `--text-fact` value, the same 44px minimum and 1px `--color-border-subtle` rule.
 * No new colour, size or radius. `DocketRow` is its sibling and opens in place; this one leaves.
 *
 * **The value is the answer, not a category**, which is `DocketRow`'s rule and the reason this
 * exists rather than a grid of cards. A springboard row reading only "Roster" has moved
 * information behind a tap and called it navigation; one reading "93 people, 61 Scouts" tells a
 * leader whether the roster needs their attention before they spend the tap. That is also why
 * {@link value} is allowed to arrive late — see the screens that use it.
 *
 * **A row with nothing to say still renders.** `value` falls back to the destination's own
 * description rather than to an empty column, because a row that vanishes while a count loads is
 * a page that reflows under the reader's thumb.
 */
export function LinkRow({
  label,
  value,
  secondary,
  icon: Icon,
  onClick,
}: {
  /** One word where possible, so it holds the 100px column at 12px uppercase. */
  label: string;
  /** What the row answers at rest. Wraps rather than shrinking, per §8. */
  value: ReactNode;
  /** The second line: what you do there, in a verb phrase. */
  secondary?: ReactNode;
  icon?: LucideIcon;
  onClick: () => void;
}) {
  return (
    <div style={{ borderBottom: '1px solid var(--color-border-subtle)' }}>
      <button
        type="button"
        onClick={onClick}
        className="grid items-start w-full text-left"
        style={{
          gridTemplateColumns: '100px 1fr auto',
          gap: 'var(--space-3)',
          minHeight: 'var(--tap-min)',
          padding: 'var(--space-3) 0',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        <span
          className="t-label flex items-center gap-2"
          style={{ color: 'var(--color-text-muted)', paddingTop: '2px' }}
        >
          {Icon && <Icon size={14} aria-hidden="true" />}
          {label}
        </span>

        <span>
          <span className="t-fact" style={{ color: 'var(--color-text-primary)', display: 'block' }}>
            {value}
          </span>
          {secondary && (
            <span
              className="t-caption"
              style={{ color: 'var(--color-text-secondary)', display: 'block' }}
            >
              {secondary}
            </span>
          )}
        </span>

        <ChevronRight
          size={18}
          aria-hidden="true"
          style={{ color: 'var(--color-text-muted)', marginTop: '2px' }}
        />
      </button>
    </div>
  );
}
